import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import IconEmailAttention from '../IconEmailAttention/IconEmailAttention';
import IconAlert from '../IconAlert/IconAlert';

import css from './EmailSpamNotice.module.css';

/**
 * Reusable banner/card/inline warning notice styled like a traditional
 * Bootstrap alert-warning field (warm amber background, border, and text).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {'card' | 'inline' | 'banner'} props.variant
 * @param {'email' | 'alert'} props.iconType
 * @param {React.ReactNode?} props.icon
 * @param {React.ReactNode?} props.message
 * @param {string?} props.messageId
 * @param {Object?} props.messageValues
 * @param {boolean?} props.showIcon
 * @param {boolean?} props.compact
 * @param {string?} props.domain
 * @returns {JSX.Element}
 */
const EmailSpamNotice = props => {
  const {
    className,
    rootClassName,
    variant = 'card',
    iconType = 'email',
    icon,
    message,
    messageId = 'EmailSpamNotice.defaultMessage',
    messageValues = {},
    showIcon = true,
    compact = false,
    domain = 'findyourkind.com',
  } = props;

  const variantClass =
    variant === 'inline'
      ? css.variantInline
      : variant === 'banner'
      ? css.variantBanner
      : css.variantCard;

  const classes = classNames(
    rootClassName || css.root,
    variantClass,
    { [css.compact]: compact },
    className
  );

  const domainElement = <strong className={css.domain}>{domain}</strong>;
  const values = { domain: domainElement, ...messageValues };

  const iconClass = classNames(css.icon, {
    [css.iconInline]: variant === 'inline',
    [css.iconAlert]: iconType === 'alert',
  });

  const renderedIcon =
    icon ||
    (iconType === 'alert' ? (
      <IconAlert className={iconClass} />
    ) : (
      <IconEmailAttention className={iconClass} />
    ));

  return (
    <div className={classes} role="alert" data-testid="email-spam-notice">
      {showIcon ? renderedIcon : null}
      <p className={css.text}>
        {message || <FormattedMessage id={messageId} values={values} />}
      </p>
    </div>
  );
};

EmailSpamNotice.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  variant: PropTypes.oneOf(['card', 'inline', 'banner']),
  iconType: PropTypes.oneOf(['email', 'alert']),
  icon: PropTypes.node,
  message: PropTypes.node,
  messageId: PropTypes.string,
  messageValues: PropTypes.object,
  showIcon: PropTypes.bool,
  compact: PropTypes.bool,
  domain: PropTypes.string,
};

export default EmailSpamNotice;
